/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FastField, Field, FieldProps } from 'formik';

import { ReviewField } from '../../components';
import { withAPIService } from '../../networking';
import { withStore } from '../../store';
import { CONTROL_TYPE } from '../../utils/constants';
import { validate } from '../../utils/validation';
import AddressAutocomplete from '../AddressAutocomplete';
import CitySelect from '../CitySelect';
import FieldAddress from '../FieldAddress';
import AddressField from '../FieldAddress/AddressField';
import FieldCheckbox from '../FieldCheckbox';
import FieldInput from '../FieldInput';
import FieldInputWithTags from '../FieldInputWithTags';
import FieldMaskedInput from '../FieldMaskedInput';
import FieldNumberFormat from '../FieldNumberFormat';
import FieldRadio from '../FieldRadio';
import FieldRequiredGroup from '../FieldRequiredGroup';
import FieldSelectWithChildren from '../FieldSelectWithChildren';
import FieldTextArea from '../FieldTextArea';
import Select from '../Select';
import StateSelect from '../StateSelect';

/* ----------------------------------
   Types
---------------------------------- */



type ValidationRule = {
  type: string;
  param: any;
};

type Store = {
  customer: Record<string, any>;
  [key: string]: any;
};

type ApiService = Record<string, (...args: any[]) => any>;

type Dispatch = (...args: any[]) => void;

interface ControlProps {
  type: string;
  name: string;
  label?: string;
  footer?: string;
  maxLength?: number;
  placeholder?: string;
  format?: string;
  mask?: string;
  required?: boolean;
  showBillTo?: boolean;
  showSoldTo?: boolean;
  showPayer?: boolean;
  onChecked?: (...args: any[]) => void;
  onCheckboxClicked?: (...args: any[]) => void;
  options?: { label: string; value: string; disabled?: boolean }[];
  validations?: ValidationRule[];
  apiService: ApiService;
  disabled?: boolean;
  category?: string;
  field?: string;
  contactCardName?: string;
  dispatch: Dispatch;
  store: Store;
  addressFields?: string[];
  requiredGroupFields?: string[];
  hasGroupError?: boolean;
  selectChildren?: any[];
  subtitle?: string;
  onChange?: (...args: any[]) => void;
  initialSelectState?: any;
  onBlur?: (...args: any[]) => void;
  onAddTag?: (...args: any[]) => void;
  getReviewFieldValue?: (...args: any[]) => any;
  hideSameAs?: boolean;
  alignSelf?: string;
  customStyles?: Record<string, any>;
  isFastField?: boolean;
  [key: string]: any;
}

/* ----------------------------------
   Helper (IDENTICAL LOGIC)
---------------------------------- */

function getActiveCard({
  name,
  validations,
  required,
  t,
  props,
  FormikFieldComponent,
  FieldComponent,
  store,
  contactCardName
}: {
  name: string;
  validations: ValidationRule[];
  required?: boolean;
  t: (key: string) => string;
  props: ControlProps;
  FormikFieldComponent: typeof Field | typeof FastField;
  FieldComponent: React.ComponentType<any>;
  store: Store;
  contactCardName?: string;
}) {
  return (
    <FormikFieldComponent
      name={name}
      id={name}
      store={store}
      validate={(value: any) =>
        validate(value, validations, !!required, t, store, contactCardName)
      }
    >
      {(formik: FieldProps) => (
        <FieldComponent formik={formik} {...props} />
      )}
    </FormikFieldComponent>
  );
}

/* ----------------------------------
   Component
---------------------------------- */

function Component(props: ControlProps) {
  const {
    type,
    options,
    name,
    required,
    label,
    footer,
    maxLength,
    placeholder,
    mask,
    contactCardName,
    format,
    dispatch,
    store,
    onChecked,
    onCheckboxClicked,
    showBillTo,
    showSoldTo,
    showPayer,
    apiService,
    addressFields,
    requiredGroupFields,
    hasGroupError,
    validations = [],
    selectChildren,
    subtitle,
    onChange,
    initialSelectState,
    disabled,
    category,
    field,
    onBlur,
    onAddTag,
    getReviewFieldValue,
    hideSameAs,
    alignSelf,
    customStyles,
    isFastField,
    ...additionalProps
  } = props;

  let FieldComponent: any;

  const [isReview, setIsReview] = useState(false);
  const [isShipTo, setIsShipTo] = useState(false);

  useEffect(() => {
    if (window.location.href.includes('review')) {
      setIsReview(true);
    }
    if (window.location.href.includes('shipTo')) {
      setIsShipTo(true);
    }
  }, [contactCardName, store]);

  let fieldProps: ControlProps = {
    name,
    type,
    label,
    footer,
    store,
    hasGroupError,
    dispatch,
    apiService,
    options,
    onChange,
    hideSameAs,
    contactCardName,
    validations,
    customStyles,
    ...additionalProps
  };

  switch (type) {
    case CONTROL_TYPE.checkbox:
      fieldProps = { ...fieldProps, onChecked, onCheckboxClicked, alignSelf };
      FieldComponent = FieldCheckbox;
      break;

    case CONTROL_TYPE.radio:
      fieldProps = { ...fieldProps, options, required };
      FieldComponent = FieldRadio;
      break;

    case CONTROL_TYPE.address:
      fieldProps = {
        ...fieldProps,
        showBillTo,
        showPayer,
        required,
        showSoldTo,
        addressFields
      };
      FieldComponent = FieldAddress;
      break;

    case CONTROL_TYPE.numberFormat:
      fieldProps = {
        ...fieldProps,
        required,
        placeholder,
        mask,
        format,
        onBlur,
        isReview,
        isShipTo
      };
      FieldComponent = FieldNumberFormat;
      break;

    case CONTROL_TYPE.addressField:
      fieldProps = { ...fieldProps, required, placeholder };
      FieldComponent = AddressField;
      break;

    case CONTROL_TYPE.addressAutoComplete:
      fieldProps = { ...fieldProps, required, placeholder };
      FieldComponent = AddressAutocomplete;
      break;

    case CONTROL_TYPE.requiredGroup:
      fieldProps = { ...fieldProps, requiredGroupFields, subtitle };
      FieldComponent = FieldRequiredGroup;
      break;

    case CONTROL_TYPE.selectWithChildren:
      fieldProps = {
        ...fieldProps,
        selectChildren,
        onChange,
        initialSelectState,
        getReviewFieldValue,
        required,
        store
      };
      FieldComponent = FieldSelectWithChildren;
      break;

    case CONTROL_TYPE.select:
      fieldProps = { ...fieldProps, required, placeholder };
      FieldComponent = Select;
      break;

    case CONTROL_TYPE.textarea:
      fieldProps = { ...fieldProps, maxLength, required, placeholder };
      FieldComponent = FieldTextArea;
      break;

    case CONTROL_TYPE.password:
      fieldProps = { ...fieldProps, required, placeholder };
      FieldComponent = FieldMaskedInput;
      break;

    case CONTROL_TYPE.inputWithTags:
      fieldProps = { ...fieldProps, required, placeholder, onAddTag };
      FieldComponent = FieldInputWithTags;
      break;

    case CONTROL_TYPE.stateSelect:
      fieldProps = { ...fieldProps, required, placeholder };
      FieldComponent = StateSelect;
      break;

    case CONTROL_TYPE.citySelect:
      fieldProps = { ...fieldProps, required, placeholder };
      FieldComponent = CitySelect;
      break;

    case CONTROL_TYPE.text:
    default:
      fieldProps = { ...fieldProps, required, placeholder };
      FieldComponent = FieldInput;
      break;
  }

  const { t } = useTranslation();
  const FormikFieldComponent = isFastField ? FastField : Field;

  if (disabled) {
    return (
      <ReviewField
        store={store}
        cardId={category!}
        dispatch={dispatch}
        config={{ ...fieldProps, category, field }}
      />
    );
  }

  return getActiveCard({
    name,
    validations,
    required,
    t,
    props: fieldProps,
    store,
    FormikFieldComponent,
    contactCardName,
    FieldComponent
  });
}

/* ----------------------------------
   HOCs
---------------------------------- */

const connectAPI = withAPIService(Component);
const Control = withStore(connectAPI);

export default Control;